<template>
  <div v-if="entries.length > 0">
    <!-- Team fill summary -->
    <div v-if="teamFillData.length > 0">
      <div
        v-for="team in teamFillData"
        :key="'fill-' + team.id"
        class="p-x-xs p-y-xxs"
      >
        <div class="flex align-items-center gap-xs">
          <span class="font-semi">{{ rosterNames[team.id] || team.name }}</span>
          <span
            v-if="team.filledCount === team.totalWeightClasses"
            class="text-green-600"
          >
            <i class="fa-solid fa-circle-check"></i>
            Full
          </span>
          <span v-else class="">
            {{ team.filledCount }}/{{ team.totalWeightClasses }} weights filled
          </span>
        </div>
        <div
          v-if="team.missingWeightClasses.length > 0"
          class="m-l-sm text-red-600"
        >
          Need: {{ team.missingWeightClasses.join(", ") }}
        </div>
      </div>
    </div>
    <hr />
    <div class="flex align-items-baseline flex-wrap m-t-md gap-xs">
      <select
        v-model="assignmentFilter"
        class="form-control input-sm text-12"
        style="width: auto"
      >
        <option value="">All</option>
        <option value="unassigned">Unassigned</option>
        <option value="assigned">Assigned</option>
      </select>
    </div>
    <!-- Empty state: all wrestlers assigned -->
    <div
      v-if="
        groupedByWeightClass.length === 0
          && assignmentFilter === 'unassigned'
      "
      class="border border-solid border-green-300 rounded-sm p-sm bg-green-50 m-t-xs"
    >
      <div class="flex align-items-center gap-xs m-b-xs">
        <i class="fa-solid fa-circle-check text-green-600"></i>
        <span class="font-semi text-green-700">
          All wrestlers assigned to teams
        </span>
      </div>
      <button
        class="btn btn-xs btn-default btn-outline"
        @click="assignmentFilter = ''"
      >
        View all wrestlers
      </button>
    </div>
    <div
      v-else
      class="border border-solid border-gray-200 rounded-sm m-t-xs"
    >
      <div
        v-for="group in groupedByWeightClass"
        :key="'g-' + group.key"
        class="p-xxs"
        style="border-bottom: 1px solid #e5e7eb"
      >
        <div class="flex align-items-center gap-xs p-x-xxs p-y-xxs">
          <span
            class="font-semi"
            :class="group.key === 'none' ? 'text-red-600' : 'text-gray-600'"
          >
            {{ group.label }}
          </span>
          <span class="text-gray-600">
            {{ group.entries.length }} wrestlers
            <template v-if="group.unassignedCount < group.entries.length">
              · {{ group.unassignedCount }} unassigned
            </template>
          </span>
        </div>
        <div class="flex flex-col m-l-xs gap-xs">
          <div v-for="entry in group.entries" :key="entry.id">
            <!-- Compact assigned row -->
            <template v-if="entry.assigned_roster">
              <div
                class="flex justify-space-between align-items-center p-x-sm p-y-xxs text-gray-500"
              >
                <span class="text-14">
                  {{ entry.wrestler_profile.full_name }}
                </span>
                <div class="flex align-items-center gap-xs">
                  <span :class="statusBadgeClass(entry)">{{
                    rosterDisplayName(entry.assigned_roster)
                  }}</span>
                  <button
                    class="btn btn-xs btn-link"
                    :disabled="entry.assigning"
                    @click="unassignWrestler(entry)"
                    title="Unassign"
                  >
                    <spinner
                      v-if="entry.assigning"
                      :inline="true"
                    ></spinner>
                    <i v-else class="fa-solid fa-xmark text-gray-400"></i>
                  </button>
                </div>
              </div>
            </template>
            <!-- Full unassigned row -->
            <template v-else>
              <div
                class="flex justify-space-between align-items-center p-sm gap-sm flex-wrap bg-yellow-50 rounded-xs"
              >
                <div class="flex align-items-center gap-xs">
                  <span :class="statusBadgeClass(entry)">{{
                    statusLabel(entry)
                  }}</span>
                  <img
                    v-if="entry.wrestler_profile.avatar_thumbnail"
                    :src="entry.wrestler_profile.avatar_thumbnail"
                    class="rounded-full flex-shrink-0 w-4 h-4"
                    style="object-fit: cover"
                  />
                  <div
                    v-else
                    class="flex align-items-center justify-center flex-shrink-0 w-6 h-6"
                  >
                    <i class="fa-solid fa-user text-gray-400"></i>
                  </div>
                  <span class="font-semi">
                    {{ entry.wrestler_profile.full_name }}
                  </span>
                </div>
                <div class="flex align-items-center gap-xs flex-shrink-0">
                  <button
                    class="btn btn-default btn-outline btn-xs"
                    @click.stop="toggleEdit(entry.wrestler_profile.id)"
                    title="Edit wrestler details"
                  >
                    <i class="fa-solid fa-pencil text-gray-400"></i> Edit
                  </button>
                  <div
                    v-if="otherDivisionsFor(entry).length > 0"
                    class="dropdown"
                  >
                    <button
                      class="btn btn-xs btn-default btn-outline dropdown-toggle"
                      data-toggle="dropdown"
                      :disabled="entry.changingDivision"
                      title="Move to another division"
                    >
                      <spinner
                        v-if="entry.changingDivision"
                        :inline="true"
                      ></spinner>
                      <span v-else>
                        Move
                        <i class="fa-solid fa-caret-down"></i>
                      </span>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-right">
                      <li
                        v-for="division in otherDivisionsFor(entry)"
                        :key="'movediv-' + entry.id + '-' + division.age_division.id"
                        :class="{ disabled: !isEligibleForDivision(entry, division) }"
                      >
                        <a
                          href="#"
                          :title="
                            isEligibleForDivision(entry, division)
                              ? `Move ${entry.wrestler_profile.full_name} to ${divisionLabel(division)}`
                              : `${entry.wrestler_profile.full_name} is not eligible for ${divisionLabel(division)}`
                          "
                          :class="{
                            'text-gray-400': !isEligibleForDivision(entry, division),
                            'cursor-not-allowed': !isEligibleForDivision(entry, division),
                          }"
                          @click.prevent="
                            isEligibleForDivision(entry, division)
                              && changeDivision(entry, division.age_division.id)
                          "
                        >
                          {{ divisionLabel(division) }}
                          <i
                            v-if="!isEligibleForDivision(entry, division)"
                            class="fa-solid fa-ban text-gray-400 m-l-xxs"
                          ></i>
                        </a>
                      </li>
                    </ul>
                  </div>
                  <!-- Must select weight class first -->
                  <template
                    v-if="
                      !entry.weight_class && weightClasses.length > 0
                    "
                  >
                    <select
                      class="form-control input-sm"
                      :disabled="entry.updatingWc"
                      @change="
                        setWeightClass(entry, $event.target.value)
                      "
                    >
                      <option value="">Select weight...</option>
                      <option
                        v-for="wc in weightClasses"
                        :key="wc.id"
                        :value="wc.id"
                      >
                        {{ wc.display_name }}
                      </option>
                    </select>
                  </template>
                  <template v-else-if="allTeams.length === 1">
                    <button
                      class="btn btn-xs btn-primary btn-outline"
                      :disabled="entry.assigning"
                      @click="assignWrestler(entry, allTeams[0].id)"
                    >
                      <spinner
                        v-if="entry.assigning"
                        :inline="true"
                      ></spinner>
                      <span v-else>Add to Team</span>
                    </button>
                  </template>
                  <template v-else-if="allTeams.length > 1">
                    <div class="dropdown">
                      <button
                        class="btn btn-xs btn-primary btn-outline dropdown-toggle"
                        data-toggle="dropdown"
                        :disabled="entry.assigning"
                      >
                        <spinner
                          v-if="entry.assigning"
                          :inline="true"
                        ></spinner>
                        <span v-else>
                          Add to Team
                          <i class="fa-solid fa-caret-down"></i>
                        </span>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-right">
                        <li v-for="team in allTeams" :key="team.id">
                          <a
                            href="#"
                            @click.prevent="
                              assignWrestler(entry, team.id)
                            "
                            >{{ team.name }}</a
                          >
                        </li>
                      </ul>
                    </div>
                  </template>
                  <!-- Remove button -->
                  <button
                    class="btn btn-xs btn-danger btn-outline"
                    :disabled="entry.removing"
                    @click="removeEntry(entry)"
                    :title="removeButtonLabel(entry)"
                    :aria-label="removeButtonLabel(entry)"
                  >
                    <spinner
                      v-if="entry.removing"
                      :inline="true"
                    ></spinner>
                    <i v-else class="fa-solid fa-trash-can"></i>
                  </button>
                </div>
              </div>
              <div class="m-t-xs">
                <!-- Inline edit form -->
                <roster-wrestler-inline-edit
                  v-if="
                    editingProfileId === entry.wrestler_profile.id
                  "
                  :member="memberForEntry(entry)"
                  class="m-t-xxs m-b-xs"
                  @saved="handleWrestlerSaved(entry, $event)"
                  @cancel="editingProfileId = null"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import Spinner from './spinner.vue';
import RosterWrestlerInlineEdit from './roster_wrestler_inline_edit.vue';

export default {
  name: 'team-registration-pool',
  components: { Spinner, RosterWrestlerInlineEdit },
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
    paidSessionId: {
      type: Number,
      required: true,
    },
    rosterNames: {
      type: Object,
      default: () => ({}),
    },
    teamFillData: {
      type: Array,
      default: () => [],
    },
    weightClasses: {
      type: Array,
      default: () => [],
    },
    allDivisions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      assignmentFilter: 'unassigned',
      editingProfileId: null,
    };
  },
  computed: {
    filteredEntries() {
      if (this.assignmentFilter === 'unassigned') {
        return this.entries.filter((e) => !e.assigned_roster);
      }
      if (this.assignmentFilter === 'assigned') {
        return this.entries.filter((e) => !!e.assigned_roster);
      }
      return this.entries;
    },
    groupedByWeightClass() {
      const groups = {};
      this.filteredEntries.forEach((entry) => {
        const missingLabel = this.missingInfoLabel(entry);
        const key = entry.weight_class
          ? entry.weight_class.id
          : `missing-${missingLabel}`;
        const label = entry.weight_class
          ? entry.weight_class.name
          : missingLabel;
        const weight = entry.weight_class
          ? entry.wrestler_profile.weight_class || 0
          : 99999;
        if (!groups[key]) {
          groups[key] = {
            key,
            label,
            weight,
            entries: [],
          };
        }
        groups[key].entries.push(entry);
      });
      return Object.values(groups)
        .sort((a, b) => a.weight - b.weight)
        .map((group) => ({
          ...group,
          unassignedCount: group.entries.filter((e) => !e.assigned_roster)
            .length,
          entries: [...group.entries].sort(
            (a, b) => (a.assigned_roster ? 1 : 0) - (b.assigned_roster ? 1 : 0),
          ),
        }));
    },
    allTeams() {
      return Object.entries(this.rosterNames).map(([id, name]) => ({
        id: parseInt(id, 10),
        name,
      }));
    },
  },
  methods: {
    removeButtonLabel(entry) {
      return entry.source === 'registration'
        ? 'Cancel registration and remove wrestler from pool'
        : 'Remove wrestler from pool';
    },
    rosterDisplayName(roster) {
      return this.rosterNames[roster.id] || roster.name;
    },
    missingInfoLabel(entry) {
      const missing = [];
      if (!entry.wrestler_profile.gender) missing.push('gender');
      if (!entry.age_division) missing.push('division');
      if (!entry.weight_class) missing.push('weight class');
      if (missing.length === 0) return 'Unassigned';
      return `Missing ${missing.join(', ')}`;
    },
    entryBgColor(entry) {
      const s = entry.registration_status;
      if (!s) return '';
      if (s === 'awaiting_approval') return 'bg-yellow-200';
      if (s === 'paid' || s === 'partially_paid' || s === 'pending') return 'bg-green-200';
      if (s === 'overdue') return 'bg-red-200';
      if (s === 'canceled') return 'bg-red-200 opacity-50';
      return '';
    },
    statusLabel(entry) {
      const s = entry.registration_status;
      if (!s) return '';
      if (s === 'awaiting_approval') return 'Applied';
      if (s === 'paid' || s === 'partially_paid' || s === 'pending') return 'Approved';
      if (s === 'overdue') return 'Overdue';
      if (s === 'canceled') return 'Payment Failed';
      return '';
    },
    statusBadgeClass(entry) {
      const s = entry.registration_status;
      if (!s) return '';
      if (s === 'awaiting_approval') return 'badge-yellow';
      if (s === 'paid' || s === 'partially_paid' || s === 'pending') return 'badge-green';
      if (s === 'overdue') return 'badge-red';
      if (s === 'canceled') return 'badge-red';
      return 'badge-gray';
    },
    setWeightClass(entry, weightClassId) {
      if (!weightClassId) return;
      const vm = this;
      const wc = vm.weightClasses.find(
        (w) => w.id === parseInt(weightClassId, 10),
      );
      if (!wc) return;

      vm.$set(entry, 'updatingWc', true);
      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${entry.id}`,
          { pool_entry: { weight_class_id: wc.id } },
        )
        .then(() => {
          vm.$set(entry, 'weight_class', {
            id: wc.id,
            name: wc.display_name,
          });
          vm.$set(entry, 'updatingWc', false);
        })
        .catch(() => {
          vm.$set(entry, 'updatingWc', false);
          vm.$notificationManager.emit(
            'show-toast',
            'Failed to set weight class',
            false,
          );
        });
    },
    assignWrestler(entry, rosterId) {
      const vm = this;
      vm.$set(entry, 'assigning', true);
      const rosterName = vm.rosterNames[rosterId] || 'Team';

      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${entry.id}/assign`,
          {
            roster_id: rosterId,
            weight_class_id: entry.weight_class ? entry.weight_class.id : null,
          },
        )
        .then(() => {
          vm.$set(entry, 'assigned_roster', { id: rosterId, name: rosterName });
          vm.$set(entry, 'assigning', false);
          vm.$notificationManager.emit(
            'show-toast',
            `${entry.wrestler_profile.full_name} assigned to ${rosterName}`,
            true,
          );
          vm.$emit('wrestler-assigned', { entryId: entry.id, rosterId, entry });
        })
        .catch((error) => {
          vm.$set(entry, 'assigning', false);
          const msg = error.response?.data?.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to assign wrestler';
          vm.$notificationManager.emit('show-toast', msg, false);
        });
    },
    divisionLabel(division) {
      const name = division.age_division?.name || '';
      const gender = division.age_division?.gender;
      if (gender === 'male') return `${name} (Boys)`;
      if (gender === 'female') return `${name} (Girls)`;
      return name;
    },
    otherDivisionsFor(entry) {
      const currentId = entry.age_division ? entry.age_division.id : null;
      return this.allDivisions.filter(
        (d) => d.age_division && d.age_division.id !== currentId,
      );
    },
    isEligibleForDivision(entry, division) {
      const eligible = entry.eligible_age_division_ids || [];
      return eligible.includes(division.age_division.id);
    },
    changeDivision(entry, ageDivisionId) {
      const vm = this;
      vm.$set(entry, 'changingDivision', true);
      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${entry.id}/change_division`,
          { age_division_id: ageDivisionId },
        )
        .then(() => {
          vm.$set(entry, 'changingDivision', false);
          vm.$notificationManager.emit(
            'show-toast',
            `${entry.wrestler_profile.full_name} moved to new division`,
            true,
          );
          vm.$emit('wrestler-division-changed', { entryId: entry.id, ageDivisionId });
        })
        .catch((error) => {
          vm.$set(entry, 'changingDivision', false);
          const msg = error.response?.data?.errors
            ? error.response.data.errors.join(', ')
            : 'Failed to move wrestler';
          vm.$notificationManager.emit('show-toast', msg, false);
        });
    },
    unassignWrestler(entry) {
      const vm = this;
      vm.$set(entry, 'assigning', true);

      axios
        .patch(
          `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${entry.id}/unassign`,
        )
        .then(() => {
          vm.$set(entry, 'assigned_roster', null);
          vm.$set(entry, 'assigning', false);
          vm.$notificationManager.emit(
            'show-toast',
            `${entry.wrestler_profile.full_name} unassigned`,
            true,
          );
          vm.$emit('wrestler-unassigned', { entryId: entry.id, entry });
        })
        .catch(() => {
          vm.$set(entry, 'assigning', false);
          vm.$notificationManager.emit(
            'show-toast',
            'Failed to unassign wrestler',
            false,
          );
        });
    },
    toggleEdit(profileId) {
      this.editingProfileId = this.editingProfileId === profileId ? null : profileId;
    },
    memberForEntry(entry) {
      return { profile: entry.wrestler_profile };
    },
    handleWrestlerSaved(entry, updatedData) {
      this.editingProfileId = null;
      // Update local entry data
      const wp = entry.wrestler_profile;
      this.$set(wp, 'weight_class', updatedData.weight_class);
      this.$set(wp, 'dob', updatedData.dob);
      this.$set(wp, 'gender', updatedData.gender);
      this.$set(wp, 'academic_class', updatedData.academic_class);
      this.$emit('wrestler-updated', entry);
    },
    removeEntry(entry) {
      const vm = this;
      const name = entry.wrestler_profile.full_name;
      const isRegistration = entry.source === 'registration';

      const confirmConfig = isRegistration ? {
        icon: 'warning',
        title: `Remove ${name}?`,
        html: `
          <p>This will:</p>
          <ul style="text-align: left;">
            <li>Void any pending invoices on their payment plan</li>
            <li>Cancel their registration</li>
            <li>Remove them from the team and pool</li>
          </ul>
          <p>Their parent can then re-register on their own account.</p>
        `,
        confirmButtonText: 'Yes, remove and cancel registration',
      } : {
        icon: 'warning',
        title: `Remove ${name} from pool?`,
        text: 'They\'ll stay on the team but be removed from this team registration.',
        confirmButtonText: 'Yes, remove',
      };

      swal.fire({
        ...confirmConfig,
        showCancelButton: true,
        reverseButtons: true,
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger m-l-md',
          cancelButton: 'btn btn-default',
        },
      }).then((result) => {
        if (!result.value) return;

        vm.$set(entry, 'removing', true);

        axios
          .delete(
            `/api/v1/paid_sessions/${vm.paidSessionId}/team_registration_pool_entries/${entry.id}`,
          )
          .then(() => {
            vm.$emit('entry-removed', entry.id);
            vm.$notificationManager.emit(
              'show-toast',
              `${name} removed`,
              true,
            );
          })
          .catch((error) => {
            vm.$set(entry, 'removing', false);
            const msg = error.response?.data?.errors
              ? error.response.data.errors.join(', ')
              : 'Failed to remove wrestler';
            vm.$notificationManager.emit('show-toast', msg, false);
          });
      });
    },
  },
};
</script>
